import { BackgroundJobKind, IBackgroundJob } from '@models/backgroundJobs/IBackgroundJob';
import { RouteNames } from '@/router/router.types';
import type { SystemLinkKind } from '@/utils/resolveSystemLink';
import type { BackgroundJobsStore } from './BackgroundJobsStore';

/** Действие с итогом задачи. Первое в списке вида — основное: оно одно в строке раздела и в превью итога. */
export type BackgroundJobAction =
    | { type: 'download'; label: string }
    | { type: 'openDocument'; label: string; docId: string }
    | { type: 'openLink'; label: string; href: string };

export type BackgroundJobActionType = BackgroundJobAction['type'];

/** Слова единицы прогресса: `count` — «12 строк», `of` — «из 150 строк» (родительный падеж: «из 21 строки»). */
export interface IBackgroundJobProgressUnit {
    count: readonly [string, string, string];
    of: readonly [string, string];
}

export interface IBackgroundJobOutcomeText {
    title: string;
    /** Текст, когда сервер ничего не сказал. */
    text: string;
}

/** Окно, которое ждёт задачу этого вида (`BackgroundJobWait`). */
export interface IBackgroundJobWaitTexts {
    /** Пока идёт запрос старта: «Передаём файл». */
    startLabel: string;
    /** Подпись лоадера, пока этап неизвестен. */
    runningLabel: string;
    /** Подсказка под лоадером, когда окно можно закрыть. */
    detachHint: string;
    /** Ответ старта без номера задачи. */
    noJobIdText: string;
    /** Задачи больше нет (уборка, чужая). */
    goneText: string;
    downloadLabels: { ok: string; err: string };
    /** Имя файла результата, когда сервер его не назвал. */
    resultFileName: string;
    outcomes: Record<'succeeded' | 'failed' | 'cancelled', IBackgroundJobOutcomeText>;
}

export interface IBackgroundJobKindView {
    /** Значок вида внутри кольца прогресса: контур SVG в системе координат 28×28 (центр 14,14). */
    iconPath: string;
    /** Заголовок строки, когда у задачи нет названия действия. */
    defaultTitle: string;
    /** Итог отказа одним-двумя словами: «Не загружен». */
    failedLabel: string;
    /** Подписи этапов вида сверх общих (`queue`, `commit`). */
    stageLabels: Readonly<Record<string, string>>;
    /** Единицы прогресса вида сверх общих (`rows`, `files`, `documents`). */
    progressUnits: Readonly<Record<string, IBackgroundJobProgressUnit>>;
    /** Подзаголовок строки раздела: имя файла и т. п. */
    subtitle: (job: IBackgroundJob) => string | null;
    /** Действия с итогом в порядке важности; у идущей и отменённой задачи их нет. */
    actions: (job: IBackgroundJob) => BackgroundJobAction[];
    /** Вид отдаёт файл результата: когда о задаче не узнать из-за сбоя, деталь уведомления всё равно предлагает его. */
    resultFile: boolean;
    /**
     * Задачи уже нет (уборка), а текст уведомления о её итоге ссылается на страницу результата: ссылка этого вида
     * из текста становится действием детали уведомления.
     */
    textLink?: { kind: SystemLinkKind; label: string };
    /** Подсказка у колокольчика, когда задача ушла в фон: где её ждать. */
    bellHint: string;
    /** Файл результата убран по сроку хранения: что делать, если он нужен. */
    fileExpiredText: string;
    wait: IBackgroundJobWaitTexts;
}

/** Запись вида: всё, чего нет, берётся из общего вида. */
export type BackgroundJobKindDefinition = Partial<Omit<IBackgroundJobKindView, 'wait'>> & {
    wait?: Partial<Omit<IBackgroundJobWaitTexts, 'outcomes'>> & {
        outcomes?: Partial<IBackgroundJobWaitTexts['outcomes']>;
    };
};

export const JOB_ACTION_LABELS: Record<BackgroundJobActionType, string> = {
    download: 'Скачать файл',
    openDocument: 'Открыть документ',
    openLink: 'Открыть',
};

export const COMMON_STAGE_LABELS: Readonly<Record<string, string>> = {
    queue: 'В очереди',
    commit: 'Сохраняем изменения',
};

export const COMMON_PROGRESS_UNITS: Readonly<Record<string, IBackgroundJobProgressUnit>> = {
    rows: { count: ['строка', 'строки', 'строк'], of: ['строки', 'строк'] },
    files: { count: ['файл', 'файла', 'файлов'], of: ['файла', 'файлов'] },
    documents: { count: ['документ', 'документа', 'документов'], of: ['документа', 'документов'] },
};

/**
 * Действия итога по умолчанию: файл результата (у отказа в нём отметки ошибок) важнее документа — с той же подписью,
 * что в окне задачи; затем ссылки итога в порядке сервера (у загрузки из файла папка с созданными документами —
 * первой); документ, из которого запускали, — если сервер не дал ссылку на него.
 */
export const defaultJobActions = (job: IBackgroundJob): BackgroundJobAction[] => {
    const actions: BackgroundJobAction[] = [];
    if (job.hasResultFile) {
        const labels = backgroundJobKind(job.kind).wait.downloadLabels;
        actions.push({ type: 'download', label: job.status === 'failed' ? labels.err : labels.ok });
    }
    const docIds = new Set<string>();
    job.links?.forEach((link) => {
        if (link.type === 'document' && link.docId && !docIds.has(link.docId)) {
            docIds.add(link.docId);
            actions.push({ type: 'openDocument', label: JOB_ACTION_LABELS.openDocument, docId: link.docId });
        } else if (link.type === 'url' && link.href) {
            actions.push({
                type: 'openLink',
                label: link.label?.trim() || JOB_ACTION_LABELS.openLink,
                href: link.href,
            });
        }
    });
    if (job.docId && docIds.size === 0) {
        actions.push({ type: 'openDocument', label: JOB_ACTION_LABELS.openDocument, docId: job.docId });
    }
    return actions;
};

const DEFAULT_KIND_VIEW: IBackgroundJobKindView = {
    iconPath: 'M14 10v4.2l2.6 1.6',
    defaultTitle: 'Фоновая задача',
    failedLabel: 'Не выполнена',
    stageLabels: {},
    progressUnits: {},
    subtitle: (job) => job.fileName || null,
    actions: defaultJobActions,
    resultFile: true,
    bellHint: 'Следить за ходом можно здесь',
    fileExpiredText: 'Срок хранения файла результата истёк. Запустите задачу заново, если файл нужен.',
    wait: {
        startLabel: 'Запускаем задачу',
        runningLabel: 'Выполняем задачу',
        detachHint: 'Окно можно закрыть — задача продолжится, о результате сообщим',
        noJobIdText: 'Сервер не вернул номер задачи. Попробуйте ещё раз.',
        goneText: 'Фоновая задача больше не найдена — её результат уже недоступен.',
        downloadLabels: { ok: 'Скачать файл', err: 'Скачать файл с ошибками' },
        resultFileName: 'Результат',
        outcomes: {
            succeeded: { title: 'Готово', text: 'Фоновая задача выполнена.' },
            failed: { title: 'Фоновая задача не выполнена', text: 'Не удалось выполнить задачу.' },
            cancelled: { title: 'Фоновая задача отменена', text: 'Фоновую задачу отменили — результата нет.' },
        },
    },
};

const REPORT_OPEN_LABEL = 'Открыть отчёт';

/** Отчёт открывается на своей странице с этим запуском: готовый — просмотром, упавший — причиной и «Построить ещё раз». */
const reportActions = (job: IBackgroundJob): BackgroundJobAction[] => {
    const href = job.links?.find((link) => link.type === 'report' && link.href)?.href;
    return href ? [{ type: 'openLink', label: REPORT_OPEN_LABEL, href }] : [];
};

/** Откуда выгружен список (`context.source` задачи экспорта): подзаголовок строки раздела. */
const GRID_EXPORT_SOURCES: Readonly<Record<string, string>> = {
    searchFolder: 'Реестр документов',
    tasks: 'Задачи',
    linkedDocs: 'Связанные документы',
};

const gridExportSubtitle = (job: IBackgroundJob): string => {
    const source = job.context?.source;
    const label =
        typeof source === 'string' && Object.hasOwn(GRID_EXPORT_SOURCES, source) ? GRID_EXPORT_SOURCES[source] : null;
    return label ? `Excel · ${label}` : 'Excel';
};

const ARCHIVE_DOWNLOAD_LABEL = 'Скачать архив';

/** Виды задач. Новый вид — запись здесь; вид без записи показывается общим видом. */
export const BACKGROUND_JOB_KINDS: Readonly<Partial<Record<BackgroundJobKind, BackgroundJobKindDefinition>>> = {
    loadFromFile: {
        iconPath: 'M14 17.5v-7M11 13l3-3 3 3M10 19h8',
        failedLabel: 'Не загружен',
        stageLabels: { import: 'Загружаем данные', hooks: 'Обновляем документ' },
        fileExpiredText: 'Срок хранения файла результата истёк. Запустите загрузку заново, если файл нужен.',
        wait: {
            startLabel: 'Передаём файл',
            runningLabel: 'Обрабатываем данные файла',
            detachHint: 'Окно можно закрыть — загрузка продолжится, о результате сообщим',
            noJobIdText: 'Сервер не вернул номер задачи загрузки. Попробуйте загрузить файл ещё раз.',
            goneText: 'Задача загрузки больше не найдена. Проверьте результат в реестре.',
            downloadLabels: { ok: 'Скачать файл результата', err: 'Скачать файл с ошибками' },
            resultFileName: 'Результат загрузки',
            outcomes: {
                succeeded: { title: 'Файл загружен', text: 'Данные из файла загружены.' },
                failed: { title: 'Файл не загружен', text: 'Не удалось загрузить данные из файла.' },
                cancelled: { title: 'Загрузка отменена', text: 'Загрузку отменили — данные из файла не загружены.' },
            },
        },
    },
    report: {
        iconPath: 'M10.5 18.5v-3M14 18.5v-8M17.5 18.5v-5.5',
        defaultTitle: 'Отчёт',
        failedLabel: 'Не построен',
        stageLabels: { data: 'Собираем данные', pages: 'Формируем страницы' },
        subtitle: (job) => (typeof job.context?.folder === 'string' && job.context.folder.trim()) || null,
        actions: reportActions,
        resultFile: false,
        textLink: { kind: 'report', label: REPORT_OPEN_LABEL },
    },
    gridExport: {
        iconPath: 'M14 9.5v7M11 13.5l3 3 3-3M10 19h8',
        defaultTitle: 'Выгрузка в Excel',
        failedLabel: 'Не выгружен',
        stageLabels: { rows: 'Выгружаем строки', file: 'Собираем файл' },
        subtitle: gridExportSubtitle,
        bellHint: 'Файл готовится — он появится здесь',
        fileExpiredText: 'Срок хранения файла выгрузки истёк. Выгрузите список заново, если файл нужен.',
        wait: {
            startLabel: 'Запускаем выгрузку',
            runningLabel: 'Готовим файл',
            detachHint: 'Окно можно закрыть — выгрузка продолжится, о готовности сообщим',
            noJobIdText: 'Сервер не вернул номер задачи выгрузки. Попробуйте выгрузить ещё раз.',
            goneText: 'Задача выгрузки больше не найдена — файл уже недоступен.',
            downloadLabels: { ok: 'Скачать файл', err: 'Скачать файл' },
            resultFileName: 'Выгрузка',
            outcomes: {
                succeeded: { title: 'Файл готов', text: 'Список выгружен.' },
                failed: { title: 'Файл не выгружен', text: 'Не удалось выгрузить список.' },
                cancelled: { title: 'Выгрузка отменена', text: 'Выгрузку отменили — файла нет.' },
            },
        },
    },
    documentReport: {
        iconPath: 'M10.5 9h4.5l2.5 2.5V19h-7zM15 9v2.5h2.5M12.7 14.4h3.1M12.7 16.8h2',
        defaultTitle: 'Отчёт по документу',
        failedLabel: 'Не сформирован',
        stageLabels: { data: 'Собираем данные', pages: 'Формируем страницы' },
        subtitle: (job) => job.resultFileName || null,
        fileExpiredText: 'Срок хранения файла отчёта истёк. Сформируйте отчёт заново, если он нужен.',
        wait: {
            startLabel: 'Запускаем формирование',
            runningLabel: 'Готовим файл',
            detachHint: 'Окно можно закрыть — отчёт сформируется, о готовности сообщим',
            noJobIdText: 'Сервер не вернул номер задачи отчёта. Попробуйте сформировать отчёт ещё раз.',
            goneText: 'Задача отчёта больше не найдена — файл уже недоступен.',
            downloadLabels: { ok: 'Скачать файл', err: 'Скачать файл' },
            resultFileName: 'Отчёт',
            outcomes: {
                succeeded: { title: 'Отчёт готов', text: 'Отчёт сформирован.' },
                failed: { title: 'Отчёт не сформирован', text: 'Не удалось сформировать отчёт.' },
                cancelled: { title: 'Отчёт отменён', text: 'Формирование отменили — файла нет.' },
            },
        },
    },
    attachmentsArchive: {
        iconPath: 'M9.5 9.5h9V12h-9zM10.5 12v6.5h7V12M12.8 14.5h2.4',
        defaultTitle: 'Архив вложений',
        failedLabel: 'Не собран',
        stageLabels: { files: 'Собираем файлы', save: 'Сохраняем архив' },
        subtitle: (job) => job.resultFileName || null,
        fileExpiredText: 'Срок хранения архива истёк. Скачайте вложения заново, если архив нужен.',
        wait: {
            startLabel: 'Запускаем сборку архива',
            runningLabel: 'Собираем файлы',
            detachHint: 'Окно можно закрыть — архив соберётся, о готовности сообщим',
            noJobIdText: 'Сервер не вернул номер задачи архива. Попробуйте скачать вложения ещё раз.',
            goneText: 'Задача архива больше не найдена — файл уже недоступен.',
            downloadLabels: { ok: ARCHIVE_DOWNLOAD_LABEL, err: ARCHIVE_DOWNLOAD_LABEL },
            resultFileName: 'Приложенные файлы.zip',
            outcomes: {
                succeeded: { title: 'Архив готов', text: 'Вложения собраны в архив.' },
                failed: { title: 'Архив не собран', text: 'Не удалось собрать архив вложений.' },
                cancelled: { title: 'Сборка отменена', text: 'Сборку архива отменили — файла нет.' },
            },
        },
    },
};

const resolveKind = (definition: BackgroundJobKindDefinition | undefined): IBackgroundJobKindView => {
    if (!definition) return DEFAULT_KIND_VIEW;
    const { wait, ...rest } = definition;
    return {
        ...DEFAULT_KIND_VIEW,
        ...rest,
        wait: {
            ...DEFAULT_KIND_VIEW.wait,
            ...wait,
            outcomes: { ...DEFAULT_KIND_VIEW.wait.outcomes, ...wait?.outcomes },
        },
    };
};

const resolved = new Map<string, IBackgroundJobKindView>();

/** Как показывать задачу вида `kind`; неизвестный вид — общим видом. */
export const backgroundJobKind = (kind: BackgroundJobKind | null | undefined): IBackgroundJobKindView => {
    const key = kind ?? '';
    let view = resolved.get(key);
    if (!view) {
        view = resolveKind(Object.hasOwn(BACKGROUND_JOB_KINDS, key) ? BACKGROUND_JOB_KINDS[key] : undefined);
        resolved.set(key, view);
    }
    return view;
};

/** Действия с итогом: только у успеха и отказа. */
export const jobActions = (job: IBackgroundJob): BackgroundJobAction[] =>
    job.status === 'succeeded' || job.status === 'failed' ? backgroundJobKind(job.kind).actions(job) : [];

/** Как выполнить действие итога там, где оно показано (раздел, превью, деталь уведомления, окно). */
export interface IBackgroundJobActionHandlers {
    /** `kind` — вид задачи: текст отказа, когда файла уже нет, а задачу клиент забыл. */
    download: (jobId: string, kind?: BackgroundJobKind | null) => unknown;
    openDocument: (docId: string, jobId: string) => void;
    openLink: (href: string, jobId: string) => void;
}

/** Основное действие итога; `null` — кнопки нет. */
export const primaryJobAction = (job: IBackgroundJob): BackgroundJobAction | null => jobActions(job)[0] ?? null;

export const runJobAction = (
    action: BackgroundJobAction,
    jobId: string,
    handlers: IBackgroundJobActionHandlers,
    kind?: BackgroundJobKind | null,
): void => {
    switch (action.type) {
        case 'download':
            void handlers.download(jobId, kind);
            return;
        case 'openDocument':
            handlers.openDocument(action.docId, jobId);
            return;
        case 'openLink':
            handlers.openLink(action.href, jobId);
    }
};

/** Ссылка итога: путь приложения — переходом в этой вкладке, внешний адрес — в новой. */
export const openJobLink = (href: string, navigate: (path: string) => void): void => {
    if (href.startsWith('/') && !href.startsWith('//')) navigate(href);
    else window.open(href, '_blank', 'noopener,noreferrer');
};

export interface IJobActionHandlersOptions {
    store: Pick<BackgroundJobsStore, 'downloadFile' | 'markSeen'>;
    /** Закрыть то, откуда ушли по ссылке итога (колокольчик). */
    close: () => void;
    navigate: (path: string) => unknown;
}

/**
 * Действия итога там, где его показывает колокольчик (раздел, превью): файл скачивается, документ и ссылка — переход
 * с закрытием колокольчика; переход — увиденный итог.
 */
export const createJobActionHandlers = ({
    store,
    close,
    navigate,
}: IJobActionHandlersOptions): IBackgroundJobActionHandlers => {
    const go = (path: string) => void navigate(path);
    return {
        download: (jobId, kind) => store.downloadFile(jobId, kind),
        openDocument: (docId, jobId) => {
            store.markSeen(jobId);
            close();
            go(RouteNames.DOCUMENT.replace(':id', docId));
        },
        openLink: (href, jobId) => {
            store.markSeen(jobId);
            close();
            openJobLink(href, go);
        },
    };
};
